import React, { useEffect, useState } from "react";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { CheckCircle2, ClipboardList, RotateCcw } from "lucide-react";
import { apiClient } from "../lib/api";
import { useAuth } from "../auth/AuthContext";

export default function CourseQuiz({ courseId, passingScore = 70, onPassed }) {
  const { user } = useAuth();
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [started, setStarted] = useState(false);
  const [answers, setAnswers] = useState({}); // question_id -> option_id
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let active = true;
    apiClient.get(`/courses/${courseId}/quiz`)
      .then((r) => { if (active) setQuiz(r.data); })
      .catch(() => { if (active) setQuiz(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [courseId]);

  if (loading) return null;
  if (!quiz || !quiz.enabled || !quiz.questions || quiz.questions.length === 0) return null;

  const total = quiz.questions.length;
  const answeredCount = Object.keys(answers).filter((qid) => !!answers[qid]).length;
  const canSubmit = answeredCount === total && !submitting;

  const submit = async () => {
    setSubmitting(true);
    setErr("");
    try {
      const payload = {
        answers: Object.entries(answers).map(([question_id, option_id]) => ({ question_id, option_id })),
      };
      const { data } = await apiClient.post(`/me/quiz/${courseId}/submit`, payload);
      setResult(data);
      if (data?.passed && onPassed) onPassed(data);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "Quiz submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setAnswers({});
    setResult(null);
    setStarted(true);
  };

  // -------- Result view --------
  if (result) {
    const passed = !!result.passed;
    return (
      <Card className="card-elevated" data-testid="card-course-quiz-result">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base font-semibold text-[#361a54]">
            <CheckCircle2 className={`h-4 w-4 ${passed ? "text-emerald-600" : "text-amber-600"}`} />
            Quiz {passed ? "passed" : "result"}
          </CardTitle>
          <CardDescription className="text-sm">
            You scored <strong data-testid="quiz-score">{result.score_percent}%</strong> ({result.correct} / {result.total} correct). Passing threshold: {result.passing_score}%.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-slate-700">
          {passed ? (
            <p className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-emerald-900" data-testid="quiz-passed-banner">
              Well done — your course has been marked complete. You can download your certificate from <strong>My learning</strong>.
            </p>
          ) : (
            <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900" data-testid="quiz-failed-banner">
              You need at least {result.passing_score}% to pass. You can retake the quiz at any time.
            </p>
          )}
          <Button size="sm" variant="outline" className="border-[#d4c5e6] bg-white text-[#361a54] hover:bg-[#f5f0fa]" onClick={reset} data-testid="quiz-retry-btn">
            <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
            Retake quiz
          </Button>
        </CardContent>
      </Card>
    );
  }

  // -------- Intro / start view --------
  if (!started) {
    return (
      <Card className="card-elevated" data-testid="card-course-quiz-intro">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base font-semibold text-[#361a54]">
            <ClipboardList className="h-4 w-4 text-[#1a8bb3]" />Course quiz
          </CardTitle>
          <CardDescription className="text-sm">
            {total} multiple-choice question{total === 1 ? "" : "s"} · pass at {passingScore}% to receive your certificate.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-slate-700">
          <p>This quiz is an alternative path to completion — passing it will mark the course as completed and unlock your certificate.</p>
          {user ? (
            <Button size="sm" className="bg-[#361a54] text-white hover:bg-[#2a1443]" onClick={() => setStarted(true)} data-testid="quiz-start-btn">
              Start quiz
            </Button>
          ) : (
            <p className="text-sm text-amber-900">Please sign in to take the quiz.</p>
          )}
        </CardContent>
      </Card>
    );
  }

  // -------- Active quiz view --------
  return (
    <Card className="card-elevated" data-testid="card-course-quiz">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base font-semibold text-[#361a54]">
          <ClipboardList className="h-4 w-4 text-[#1a8bb3]" />Course quiz
        </CardTitle>
        <CardDescription className="text-sm">
          {answeredCount} of {total} answered · pass at {passingScore}%
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5 text-sm text-slate-700">
        {quiz.questions.map((q, qi) => (
          <div key={q.question_id} className="space-y-2 rounded-lg border border-[#e8e0f0] bg-white/70 p-3" data-testid={`quiz-question-${qi}`}>
            <p className="text-sm font-semibold text-[#361a54]">{qi + 1}. {q.prompt}</p>
            <div className="space-y-1">
              {(q.options || []).map((opt, oi) => {
                const checked = answers[q.question_id] === opt.option_id;
                return (
                  <label
                    key={opt.option_id}
                    className={`flex cursor-pointer items-start gap-2 rounded-md px-2 py-1.5 transition ${checked ? "bg-[#efe6f8] text-[#361a54]" : "hover:bg-[#f5f0fa]/60"}`}
                  >
                    <input
                      type="radio"
                      name={`q-${q.question_id}`}
                      value={opt.option_id}
                      checked={checked}
                      onChange={() => setAnswers((p) => ({ ...p, [q.question_id]: opt.option_id }))}
                      className="mt-1"
                      data-testid={`quiz-option-${qi}-${oi}`}
                    />
                    <span className="text-sm">{opt.text}</span>
                  </label>
                );
              })}
            </div>
          </div>
        ))}
        {err && (
          <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="quiz-error">{err}</p>
        )}
        <div className="flex items-center justify-end gap-2">
          <Button size="sm" variant="outline" className="border-[#d4c5e6] bg-white text-[#361a54] hover:bg-[#f5f0fa]" onClick={() => { setStarted(false); setAnswers({}); }}>
            Cancel
          </Button>
          <Button size="sm" className="bg-[#361a54] text-white hover:bg-[#2a1443] disabled:opacity-60" disabled={!canSubmit} onClick={submit} data-testid="quiz-submit-btn">
            {submitting ? "Submitting…" : "Submit quiz"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
